import React, { useState } from 'react';
import { Box, ButtonBase, IconButton, Menu, MenuItem, ListItemIcon } from '@mui/material';
import {
  Add as AddIcon,
  DarkModeOutlined as DarkIcon,
  LightModeOutlined as LightIcon,
  Logout as LogoutIcon,
  Link as LinkIcon,
  Translate as TranslateIcon,
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BrandMark } from './BrandMark';
import { LanguageToggle } from './LanguageToggle';
import { NAV_ITEMS, sectionForPath } from './appNavItems';
import { displayNameOf } from './useAppShellData';
import { InitialsAvatar } from '../ui/InitialsAvatar';
import { useColorMode } from '../../theme/ColorModeProvider';
import { authService } from '../../services/auth.service';
import { UserProfile } from '../../types/user.types';

export const MobileTopBar = ({ profile }: { profile: UserProfile | null }) => {
  const { t, i18n } = useTranslation('layout');
  const navigate = useNavigate();
  const { mode, toggleMode } = useColorMode();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const stravaConnected = profile?.stravaProfile != null;

  return (
    <Box component="header" sx={{ flex: 'none', p: '12px 16px 10px', display: 'flex', alignItems: 'center', gap: 1.25, backgroundColor: 'var(--card)', borderBottom: '1px solid var(--border)' }}>
      <BrandMark size={30} />
      <Box component="span" sx={{ fontWeight: 700, fontSize: 16, letterSpacing: '.02em' }}>URACE</Box>
      <Box sx={{ ml: 'auto', display: 'flex' }}>
        <LanguageToggle size={36} />
      </Box>
      <IconButton onClick={toggleMode} aria-label={t('common:theme.toggle')} sx={{ width: 36, height: 36, border: '1px solid var(--border)', borderRadius: '9px', color: 'var(--fg)' }}>
        {mode === 'dark' ? <LightIcon sx={{ fontSize: 18 }} /> : <DarkIcon sx={{ fontSize: 18 }} />}
      </IconButton>
      <ButtonBase onClick={e => setAnchor(e.currentTarget)} aria-label={t('account.menu')} sx={{ borderRadius: '999px' }}>
        <InitialsAvatar name={displayNameOf(profile) || '?'} size={36} />
      </ButtonBase>
      <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
        {!stravaConnected && (
          <MenuItem onClick={() => { setAnchor(null); navigate('/connect-strava'); }}>
            <ListItemIcon><LinkIcon fontSize="small" /></ListItemIcon>{t('strava.connect')}
          </MenuItem>
        )}
        <MenuItem onClick={() => i18n.changeLanguage(i18n.language === 'vi' ? 'en' : 'vi')}>
          <ListItemIcon><TranslateIcon fontSize="small" /></ListItemIcon>
          {t('common:language.label')}: {t(i18n.language === 'vi' ? 'common:language.vi' : 'common:language.en')}
        </MenuItem>
        <MenuItem onClick={() => { setAnchor(null); authService.logout(); }}>
          <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>{t('account.logout')}
        </MenuItem>
      </Menu>
    </Box>
  );
};

// Five-slot tab bar; the centre slot opens the new-challenge picker.
export const MobileBottomNav = ({ onNewChallenge }: { onNewChallenge: () => void }) => {
  const { t } = useTranslation('layout');
  const navigate = useNavigate();
  const section = sectionForPath(useLocation().pathname);
  const [home, groups, activities, profile] = NAV_ITEMS;
  const slots = [home, groups, null, activities, profile];

  return (
    <Box component="nav" sx={{ flex: 'none', display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', borderTop: '1px solid var(--border)', backgroundColor: 'var(--card)', p: '6px 6px calc(10px + env(safe-area-inset-bottom))' }}>
      {slots.map((item, i) => {
        if (!item) {
          return (
            <ButtonBase key="new" onClick={onNewChallenge} sx={{ height: 52, flexDirection: 'column', gap: 0.5, fontSize: 11, fontWeight: 500, color: 'var(--muted)' }}>
              <Box component="span" sx={{ width: 36, height: 36, borderRadius: '12px', background: 'var(--grad)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <AddIcon sx={{ fontSize: 20 }} />
              </Box>
              {t('nav.short.new')}
            </ButtonBase>
          );
        }
        const active = section === item.key;
        return (
          <ButtonBase
            key={item.key}
            onClick={() => navigate(item.to)}
            aria-current={active ? 'page' : undefined}
            sx={{ height: 52, flexDirection: 'column', gap: 0.5, fontSize: 11, fontWeight: 500, color: active ? 'var(--accent-fg)' : 'var(--muted)' }}
          >
            <Box component="span" sx={{ width: 30, height: 30, borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: active ? 'var(--accent-bg)' : 'transparent', '& svg': { fontSize: 19 } }}>
              {item.icon}
            </Box>
            {t(item.shortLabelKey)}
          </ButtonBase>
        );
      })}
    </Box>
  );
};
